import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATTACHMENT_ACCEPT,
  FORECAST_CERTAINTIES,
  FORECAST_CUSTOM_UNITS,
  FORECAST_DIRECTIONS,
  FORECAST_FREQUENCIES,
  FORECAST_NATURES,
  type ForecastFrequency,
} from '@loot/shared';
import { Download, Paperclip } from 'lucide-react';
import {
  api,
  type ApiError,
  type CashAccount,
  type CashForecast,
  type CashForecastOccurrence,
  type CashSettingsEntity,
  type ForecastFormOptions,
} from '../../api.ts';
import { checkFile } from '../purchasing/Attachments.tsx';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  Panel,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { normalizeDecimal } from '../fpa/expense-form.ts';
import { useFormat } from '../purchasing/common.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';

const KEY = ['finance', 'treasury', 'forecasts'];
type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';
const STATUS_TONE: Record<CashForecastOccurrence['status'], Tone> = {
  open: 'info',
  closed: 'neutral',
  cancelled: 'off',
};

type Dialog =
  | { kind: 'new' }
  | { kind: 'edit'; occurrence: CashForecastOccurrence; forecast: CashForecast }
  | { kind: 'link'; occurrence: CashForecastOccurrence; forecast: CashForecast }
  | { kind: 'reason'; action: 'cancel' | 'close' | 'undo'; path: string };

/**
 * Lançamentos previstos (ADR 0084, Entrega 4): previsões manuais e recorrentes da empresa, cada
 * ocorrência com a sobra ainda prevista. O vínculo com o título do Contas a Pagar faz o título
 * representar a previsão na projeção, sem contar duas vezes.
 */
export function TreasuryForecasts() {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState('');
  const settings = useQuery({
    queryKey: ['finance', 'treasury', 'projection', 'settings'],
    queryFn: () => api.get<{ entities: CashSettingsEntity[] }>('/finance/treasury/settings'),
  });
  if (settings.isPending) return <Loading />;
  if (settings.isError) return <ErrorBox error={settings.error} />;
  const entities = settings.data.entities;
  if (entities.length === 0) {
    return (
      <Panel>
        <p className="text-sm text-muted">{t('finance.flow.noEntities')}</p>
      </Panel>
    );
  }
  const entity = entities.find((e) => e.legalEntityId === entityId) ?? entities[0]!;
  return <Forecasts entity={entity} entities={entities} onEntity={setEntityId} />;
}

function Forecasts({
  entity,
  entities,
  onEntity,
}: {
  entity: CashSettingsEntity;
  entities: CashSettingsEntity[];
  onEntity: (id: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [open, setOpen] = useState<string | null>(null);
  const [onlyActive, setOnlyActive] = useState(true);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const data = useQuery({
    queryKey: [...KEY, entity.legalEntityId],
    queryFn: () =>
      api.get<{ canManage: boolean; seesPayroll: boolean; forecasts: CashForecast[] }>(
        `/finance/treasury/forecasts?legalEntityId=${entity.legalEntityId}`,
      ),
  });
  const list = data.data?.forecasts ?? [];
  const visible = onlyActive ? list.filter((f) => f.status === 'active') : list;
  const canManage = data.data?.canManage ?? false;
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar
        action={
          canManage && (
            <Button onClick={() => setDialog({ kind: 'new' })}>{t('finance.forecasts.new')}</Button>
          )
        }
      >
        <Select
          id="forecast-entity"
          aria-label={t('finance.flow.fields.entity')}
          value={entity.legalEntityId}
          onChange={(e) => onEntity(e.target.value)}
        >
          {entities.map((e) => (
            <option key={e.legalEntityId} value={e.legalEntityId}>
              {e.code} · {e.name}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
          />
          {t('finance.forecasts.onlyActive')}
        </label>
      </ListToolbar>
      <Panel title={t('finance.forecasts.title')}>
        <p className="text-sm text-muted">{t('finance.forecasts.about')}</p>
        {data.isSuccess && !data.data.seesPayroll && (
          <p className="text-sm text-muted">{t('finance.forecasts.payrollHidden')}</p>
        )}
        {data.isPending && <Loading />}
        {data.isError && <ErrorBox error={data.error} />}
        {data.isSuccess && (
          <Table
            head={[
              t('finance.forecasts.fields.description'),
              t('finance.forecasts.fields.direction'),
              { label: t('finance.forecasts.fields.amount'), align: 'right' },
              t('finance.forecasts.fields.frequency'),
              t('finance.forecasts.fields.certainty'),
              t('finance.forecasts.fields.next'),
              t('finance.forecasts.fields.status'),
              '',
            ]}
          >
            {visible.length === 0 && <EmptyRow colSpan={8}>{t('finance.forecasts.none')}</EmptyRow>}
            {visible.map((f) => {
              const next = f.occurrences.find((o) => o.status === 'open');
              return (
                <Fragment key={f.id}>
                  <tr>
                    <td>
                      <button
                        type="button"
                        className="text-left underline decoration-dotted"
                        aria-expanded={open === f.id}
                        onClick={() => setOpen(open === f.id ? null : f.id)}
                      >
                        {f.description}
                      </button>
                      {f.isPayroll && (
                        <span className="ml-2">
                          <Badge tone="info">{t('finance.forecasts.payroll')}</Badge>
                        </span>
                      )}
                      {f.previousId && (
                        <div className="text-xs text-muted">{t('finance.forecasts.replaces')}</div>
                      )}
                    </td>
                    <td>
                      <Badge
                        tone={
                          f.direction === 'in' ? 'ok' : f.direction === 'out' ? 'accent' : 'info'
                        }
                      >
                        {t(`finance.forecasts.directions.${f.direction}`)}
                      </Badge>
                    </td>
                    <NumCell>{fmt.money(f.amount, f.currency)}</NumCell>
                    <td>
                      {f.frequency === 'custom'
                        ? t('finance.forecasts.customLabel', {
                            n: f.customEvery,
                            unit: t(`finance.forecasts.customUnits.${f.customUnit}`),
                          })
                        : t(`finance.forecasts.frequencies.${f.frequency}`)}
                    </td>
                    <td>{t(`finance.forecasts.certainties.${f.certainty}`)}</td>
                    <td className="whitespace-nowrap">{next ? fmt.date(next.dueDate) : '—'}</td>
                    <td>
                      <Badge tone={f.status === 'active' ? 'ok' : 'off'}>
                        {t(`finance.forecasts.statuses.${f.status}`)}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {canManage && f.status === 'active' && (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setDialog({
                              kind: 'reason',
                              action: 'cancel',
                              path: `/forecasts/${f.id}/cancel`,
                            })
                          }
                        >
                          {t('finance.forecasts.cancel')}
                        </Button>
                      )}
                      <MasterDeleteButton table="treasury.forecast" id={f.id} compact />
                    </td>
                  </tr>
                  {open === f.id && (
                    <tr>
                      <td colSpan={8} className="bg-paper">
                        <Occurrences
                          forecast={f}
                          canManage={canManage && f.status === 'active'}
                          onDialog={setDialog}
                        />
                        <ForecastAttachments forecast={f} canManage={canManage} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>
        )}
      </Panel>
      {dialog?.kind === 'new' && <ForecastForm entity={entity} onDone={() => setDialog(null)} />}
      {dialog?.kind === 'edit' && (
        <EditForm
          occurrence={dialog.occurrence}
          forecast={dialog.forecast}
          onDone={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'link' && (
        <LinkForm
          entityId={entity.legalEntityId}
          occurrence={dialog.occurrence}
          forecast={dialog.forecast}
          onDone={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'reason' && (
        <ReasonForm action={dialog.action} path={dialog.path} onDone={() => setDialog(null)} />
      )}
    </div>
  );
}

function Occurrences({
  forecast: f,
  canManage,
  onDialog,
}: {
  forecast: CashForecast;
  canManage: boolean;
  onDialog: (d: Dialog) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const money = (v: string) => fmt.money(v, f.currency);
  return (
    <ul className="flex flex-col gap-2 text-sm">
      {f.occurrences.map((o) => (
        <li key={o.id} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono">#{o.sequence}</span>
            <span>{fmt.date(o.dueDate)}</span>
            <span className="font-mono">{money(o.amount)}</span>
            <Badge tone={STATUS_TONE[o.status]}>
              {o.status === 'open' && o.residual === '0.0000'
                ? t('finance.forecasts.occurrenceStatuses.linked')
                : t(`finance.forecasts.occurrenceStatuses.${o.status}`)}
            </Badge>
            {o.overridden && (
              <span className="text-xs text-muted">{t('finance.forecasts.overridden')}</span>
            )}
            {o.linked !== '0.0000' && (
              <span className="text-muted">
                {t('finance.forecasts.linkedResidual', {
                  linked: money(o.linked),
                  residual: money(o.residual),
                })}
              </span>
            )}
            {o.closeReason && <span className="text-muted">· {o.closeReason}</span>}
            {canManage && o.status === 'open' && (
              <span className="ml-auto flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => onDialog({ kind: 'edit', occurrence: o, forecast: f })}
                >
                  {t('finance.forecasts.edit')}
                </Button>
                {o.residual !== '0.0000' && (
                  <Button
                    variant="secondary"
                    onClick={() => onDialog({ kind: 'link', occurrence: o, forecast: f })}
                  >
                    {t('finance.forecasts.link')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() =>
                    onDialog({
                      kind: 'reason',
                      action: 'close',
                      path: `/forecast-occurrences/${o.id}/close`,
                    })
                  }
                >
                  {t('finance.forecasts.close')}
                </Button>
              </span>
            )}
          </div>
          {o.links.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-2 pl-6 text-muted">
              <span>{l.label ?? '—'}</span>
              <span className="font-mono">{money(l.amount)}</span>
              {l.undoneAt ? (
                <span>{t('finance.forecasts.undone', { reason: l.undoReason ?? '' })}</span>
              ) : (
                canManage &&
                o.status === 'open' && (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      onDialog({
                        kind: 'reason',
                        action: 'undo',
                        path: `/forecast-links/${l.id}/undo`,
                      })
                    }
                  >
                    {t('finance.forecasts.undo')}
                  </Button>
                )
              )}
            </div>
          ))}
        </li>
      ))}
    </ul>
  );
}

function useForecastAction<T = unknown>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: T) => api.post<Record<string, unknown>>(`/finance/treasury${path}`, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['finance', 'treasury'] });
    },
  });
}

function ForecastForm({ entity, onDone }: { entity: CashSettingsEntity; onDone: () => void }) {
  const { t } = useTranslation();
  const save = useForecastAction('/forecasts');
  const accounts = useQuery({
    queryKey: ['finance', 'treasury', 'accounts'],
    queryFn: () => api.get<{ accounts: CashAccount[] }>('/finance/treasury/accounts'),
  });
  const options = useQuery({
    queryKey: ['finance', 'treasury', 'forecast-options', entity.legalEntityId],
    queryFn: () =>
      api.get<ForecastFormOptions>(
        `/finance/treasury/forecast-form-options?legalEntityId=${entity.legalEntityId}`,
      ),
  });
  const [start] = useState(() => ({
    direction: 'out',
    description: '',
    amount: '',
    currency: entity.functionalCurrency,
    startDate: '',
    competence: '',
    accountKey: '',
    toAccountKey: '',
    expenseTypeId: '',
    classificationId: '',
    costCenterId: '',
    partnerId: '',
    nature: 'operating',
    certainty: 'probable',
    frequency: 'once' as ForecastFrequency,
    customUnit: 'month',
    customEvery: '',
    endDate: '',
    maxOccurrences: '',
    notes: '',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const own = (accounts.data?.accounts ?? []).filter(
    (a) => a.legalEntityId === entity.legalEntityId && a.currency === f.currency.toUpperCase(),
  );
  const recurring = f.frequency !== 'once';
  const transfer = f.direction === 'transfer';
  const o = options.data;
  const chosen = o?.classifications.find((c) => c.id === f.classificationId);
  const expenseTypes = (o?.expenseTypes ?? []).filter(
    (e) => !chosen || chosen.expenseTypeIds === null || chosen.expenseTypeIds.includes(e.id),
  );
  const payroll =
    !!chosen?.isPayroll || !!o?.expenseTypes.find((e) => e.id === f.expenseTypeId)?.isPayroll;
  const pick = (
    id: 'classificationId' | 'expenseTypeId' | 'costCenterId' | 'partnerId',
    items: { id: string; label: string }[],
  ) => (
    <Field label={t(`finance.forecasts.fields.${id}`)}>
      <Select id={`fc-${id}`} value={f[id]} onChange={set(id)}>
        <option value="">—</option>
        {items.map((i) => (
          <option key={i.id} value={i.id}>
            {i.label}
          </option>
        ))}
      </Select>
    </Field>
  );
  return (
    <FormModal
      open
      title={t('finance.forecasts.new')}
      description={`${entity.code} · ${entity.name}`}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            legalEntityId: entity.legalEntityId,
            direction: f.direction,
            description: f.description,
            amount: normalizeDecimal(f.amount),
            currency: f.currency.trim().toUpperCase(),
            startDate: f.startDate,
            competence: f.competence || null,
            accountKey: f.accountKey || null,
            toAccountKey: transfer ? f.toAccountKey || null : null,
            expenseTypeId: transfer ? null : f.expenseTypeId || null,
            classificationId: transfer ? null : f.classificationId || null,
            costCenterId: transfer ? null : f.costCenterId || null,
            partnerId: transfer ? null : f.partnerId || null,
            nature: f.nature,
            certainty: f.certainty,
            frequency: f.frequency,
            customUnit: f.frequency === 'custom' ? f.customUnit : null,
            customEvery: f.frequency === 'custom' && f.customEvery ? Number(f.customEvery) : null,
            endDate: recurring && f.endDate ? f.endDate : null,
            maxOccurrences: recurring && f.maxOccurrences ? Number(f.maxOccurrences) : null,
            notes: f.notes,
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.forecasts.fields.direction')} required>
          <Select id="fc-direction" value={f.direction} onChange={set('direction')}>
            {FORECAST_DIRECTIONS.map((d) => (
              <option key={d} value={d}>
                {t(`finance.forecasts.directions.${d}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.forecasts.fields.description')} required>
          <Input
            id="fc-description"
            required
            maxLength={300}
            value={f.description}
            onChange={set('description')}
          />
        </Field>
        <Field label={t('finance.forecasts.fields.amount')} required>
          <Input
            id="fc-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={set('amount')}
          />
        </Field>
        <Field label={t('finance.forecasts.fields.currency')} required>
          <CurrencySelect
            id="fc-currency"
            value={f.currency}
            onChange={(code) => setF((s) => ({ ...s, currency: code }))}
          />
        </Field>
        <Field label={t('finance.forecasts.fields.startDate')} required>
          <Input
            id="fc-start"
            type="date"
            required
            value={f.startDate}
            onChange={set('startDate')}
          />
        </Field>
        <Field
          label={t('finance.forecasts.fields.competence')}
          hint={t('finance.forecasts.competenceHint')}
        >
          <Input
            id="fc-competence"
            type="month"
            value={f.competence}
            onChange={set('competence')}
          />
        </Field>
        <Field
          label={t(
            transfer ? 'finance.forecasts.fields.fromAccount' : 'finance.forecasts.fields.account',
          )}
          required={transfer}
        >
          <Select
            id="fc-account"
            required={transfer}
            value={f.accountKey}
            onChange={set('accountKey')}
          >
            <option value="">{transfer ? '—' : t('finance.flow.noAccount')}</option>
            {own.map((a) => (
              <option key={a.accountKey} value={a.accountKey}>
                {a.nickname} · {a.masked}
              </option>
            ))}
          </Select>
        </Field>
        {transfer && (
          <Field label={t('finance.forecasts.fields.toAccount')} required>
            <Select
              id="fc-to-account"
              required
              value={f.toAccountKey}
              onChange={set('toAccountKey')}
            >
              <option value="">—</option>
              {own
                .filter((a) => a.accountKey !== f.accountKey)
                .map((a) => (
                  <option key={a.accountKey} value={a.accountKey}>
                    {a.nickname} · {a.masked}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        {!transfer && (
          <>
            {pick(
              'classificationId',
              (o?.classifications ?? []).map((c) => ({ id: c.id, label: c.name })),
            )}
            {pick(
              'expenseTypeId',
              expenseTypes.map((e) => ({ id: e.id, label: e.name })),
            )}
            {pick(
              'costCenterId',
              (o?.costCenters ?? []).map((c) => ({ id: c.id, label: `${c.code} · ${c.name}` })),
            )}
            {pick(
              'partnerId',
              (o?.partners ?? []).map((p) => ({ id: p.id, label: p.name })),
            )}
          </>
        )}
        {payroll && (
          <p className="text-sm text-muted sm:col-span-2">{t('finance.forecasts.payrollHint')}</p>
        )}
        <Field label={t('finance.forecasts.fields.nature')} required>
          <Select id="fc-nature" value={f.nature} onChange={set('nature')}>
            {FORECAST_NATURES.map((n) => (
              <option key={n} value={n}>
                {t(`finance.forecasts.natures.${n}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.forecasts.fields.certainty')} required>
          <Select id="fc-certainty" value={f.certainty} onChange={set('certainty')}>
            {FORECAST_CERTAINTIES.map((c) => (
              <option key={c} value={c}>
                {t(`finance.forecasts.certainties.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>
      <FormSection title={t('finance.forecasts.recurrence')}>
        <Field label={t('finance.forecasts.fields.frequency')} required>
          <Select id="fc-frequency" value={f.frequency} onChange={set('frequency')}>
            {FORECAST_FREQUENCIES.map((q) => (
              <option key={q} value={q}>
                {t(`finance.forecasts.frequencies.${q}`)}
              </option>
            ))}
          </Select>
        </Field>
        {f.frequency === 'custom' && (
          <>
            <Field label={t('finance.forecasts.fields.customEvery')} required>
              <Input
                id="fc-every"
                inputMode="numeric"
                required
                value={f.customEvery}
                onChange={set('customEvery')}
              />
            </Field>
            <Field label={t('finance.forecasts.fields.customUnit')} required>
              <Select id="fc-unit" value={f.customUnit} onChange={set('customUnit')}>
                {FORECAST_CUSTOM_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {t(`finance.forecasts.customUnits.${u}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        {recurring && (
          <>
            <Field
              label={t('finance.forecasts.fields.endDate')}
              hint={t('finance.forecasts.endHint')}
            >
              <Input id="fc-end" type="date" value={f.endDate} onChange={set('endDate')} />
            </Field>
            <Field label={t('finance.forecasts.fields.maxOccurrences')}>
              <Input
                id="fc-max"
                inputMode="numeric"
                value={f.maxOccurrences}
                onChange={set('maxOccurrences')}
              />
            </Field>
          </>
        )}
      </FormSection>
      <FormSection columns={1}>
        <Field label={t('finance.forecasts.fields.notes')}>
          <Textarea id="fc-notes" maxLength={2000} value={f.notes} onChange={set('notes')} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function EditForm({
  occurrence: o,
  forecast,
  onDone,
}: {
  occurrence: CashForecastOccurrence;
  forecast: CashForecast;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const save = useForecastAction(`/forecast-occurrences/${o.id}/edit`);
  const [start] = useState(() => ({
    scope: 'this',
    dueDate: o.dueDate,
    amount: o.amount.replace(/\.?0+$/, ''),
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <FormModal
      open
      size="md"
      title={t('finance.forecasts.editTitle', { sequence: o.sequence })}
      description={forecast.description}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            scope: f.scope,
            ...(f.dueDate !== o.dueDate ? { dueDate: f.dueDate } : {}),
            amount: normalizeDecimal(f.amount),
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.forecasts.fields.scope')} required>
          <Select id="fc-scope" value={f.scope} onChange={set('scope')}>
            <option value="this">{t('finance.forecasts.scopes.this')}</option>
            {forecast.frequency !== 'once' && (
              <option value="following">{t('finance.forecasts.scopes.following')}</option>
            )}
          </Select>
        </Field>
        <Field label={t('finance.forecasts.fields.dueDate')} required>
          <Input id="fc-due" type="date" required value={f.dueDate} onChange={set('dueDate')} />
        </Field>
        <Field label={t('finance.forecasts.fields.amount')} required>
          <Input
            id="fc-edit-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={set('amount')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function LinkForm({
  entityId,
  occurrence: o,
  forecast,
  onDone,
}: {
  entityId: string;
  occurrence: CashForecastOccurrence;
  forecast: CashForecast;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const save = useForecastAction(`/forecast-occurrences/${o.id}/links`);
  const options = useQuery({
    queryKey: ['finance', 'treasury', 'forecast-link-options', entityId],
    queryFn: () =>
      api.get<{
        installments: {
          id: string;
          label: string;
          currency: string;
          openAmount: string;
          dueDate: string;
        }[];
        receipts: {
          id: string;
          label: string;
          currency: string;
          amount: string;
          date: string;
          status: string;
        }[];
      }>(`/finance/treasury/forecast-link-options?legalEntityId=${entityId}`),
  });
  // O registro real: parcela do Contas a Pagar ("i:") ou recebimento ("r:").
  const [start] = useState(() => ({
    target: '',
    amount: o.residual.replace(/\.?0+$/, ''),
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const usable = (options.data?.installments ?? []).filter((i) => i.currency === forecast.currency);
  const receipts = (options.data?.receipts ?? []).filter((r) => r.currency === forecast.currency);
  return (
    <FormModal
      open
      size="md"
      title={t('finance.forecasts.linkTitle')}
      description={t('finance.forecasts.linkHint')}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            ...(f.target.startsWith('r:')
              ? { receiptId: f.target.slice(2) }
              : { installmentId: f.target.slice(2) }),
            amount: normalizeDecimal(f.amount),
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection columns={1}>
        {options.isError && <ErrorBox error={options.error} />}
        <Field label={t('finance.forecasts.fields.target')} required>
          <Select id="fc-installment" required value={f.target} onChange={set('target')}>
            <option value="">—</option>
            {usable.length > 0 && (
              <optgroup label={t('finance.forecasts.fields.installment')}>
                {usable.map((i) => (
                  <option key={i.id} value={`i:${i.id}`}>
                    {i.label} · {fmt.date(i.dueDate)} · {fmt.money(i.openAmount, i.currency)}
                  </option>
                ))}
              </optgroup>
            )}
            {receipts.length > 0 && (
              <optgroup label={t('finance.forecasts.fields.receipt')}>
                {receipts.map((r) => (
                  <option key={r.id} value={`r:${r.id}`}>
                    {r.label} · {fmt.date(r.date)} · {fmt.money(r.amount, r.currency)}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Field>
        <Field label={t('finance.forecasts.fields.linkAmount')} required>
          <Input
            id="fc-link-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={set('amount')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function ReasonForm({
  action,
  path,
  onDone,
}: {
  action: 'cancel' | 'close' | 'undo';
  path: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const save = useForecastAction(path);
  const [reason, setReason] = useState('');
  return (
    <FormModal
      open
      size="md"
      title={t(`finance.forecasts.reasonTitles.${action}`)}
      dirty={reason !== ''}
      saving={save.isPending}
      error={save.error}
      saveTone={action === 'cancel' ? 'danger' : 'primary'}
      onSave={() => save.mutate({ reason }, { onSuccess: onDone })}
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.forecasts.fields.reason')} required>
          <Textarea
            id="fc-reason"
            required
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Anexos da previsão (contrato, boleto, memória de cálculo): baixar, anexar e retirar. */
function ForecastAttachments({
  forecast: f,
  canManage,
}: {
  forecast: CashForecast;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [attaching, setAttaching] = useState(false);
  const url = (id: string) => `/api/finance/treasury/forecasts/${f.id}/attachments/${id}`;
  return (
    <div className="mt-4 flex flex-col gap-2 text-sm">
      <h4 className="font-medium">{t('finance.forecasts.attachments.title')}</h4>
      {f.attachments.length === 0 ? (
        <p className="text-muted">{t('finance.forecasts.attachments.none')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-rule">
          {f.attachments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 py-2">
              <Paperclip size={16} aria-hidden className="text-muted" />
              <div className="flex min-w-0 flex-1 flex-col">
                <a href={url(a.id)} className="truncate underline">
                  {a.title ?? a.fileName}
                </a>
                <span className="text-xs text-muted">
                  {a.fileName} · {fmt.bytes(a.sizeBytes)} · {fmt.dateTime(a.createdAt)}
                </span>
              </div>
              <a
                href={url(a.id)}
                className="inline-flex items-center gap-1 text-muted hover:text-ink"
              >
                <Download size={16} aria-hidden /> {t('finance.forecasts.attachments.download')}
              </a>
              {canManage && <RemoveAttachment forecastId={f.id} attachmentId={a.id} />}
            </li>
          ))}
        </ul>
      )}
      {canManage && (
        <div>
          <Button variant="secondary" onClick={() => setAttaching(true)}>
            <Paperclip size={16} aria-hidden /> {t('finance.forecasts.attachments.attach')}
          </Button>
        </div>
      )}
      {attaching && <AttachForm forecastId={f.id} onDone={() => setAttaching(false)} />}
    </div>
  );
}

/** Retira o anexo da lista; o registro fica no histórico. */
function RemoveAttachment({
  forecastId,
  attachmentId,
}: {
  forecastId: string;
  attachmentId: string;
}) {
  const { t } = useTranslation();
  const remove = useForecastAction(`/forecasts/${forecastId}/attachments/${attachmentId}/remove`);
  return (
    <Button variant="secondary" disabled={remove.isPending} onClick={() => remove.mutate({})}>
      {t('finance.forecasts.attachments.remove')}
    </Button>
  );
}

function AttachForm({ forecastId, onDone }: { forecastId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [checkError, setCheckError] = useState<ApiError | null>(null);
  const send = useMutation({
    mutationFn: (file: File) => {
      // O título vai antes do arquivo, como o servidor espera.
      const form = new FormData();
      if (title.trim()) form.append('title', title.trim());
      form.append('file', file, file.name);
      return api.upload(`/finance/treasury/forecasts/${forecastId}/attachments`, form);
    },
    onSuccess: async () => {
      onDone();
      await qc.invalidateQueries({ queryKey: ['finance', 'treasury'] });
    },
  });
  return (
    <FormModal
      open
      size="md"
      title={t('finance.forecasts.attachments.attach')}
      saveLabel={t('finance.forecasts.attachments.send')}
      dirty={!!file || title.trim() !== ''}
      saving={send.isPending}
      error={checkError ?? send.error}
      onSave={() => {
        if (!file) return;
        const problem = checkFile(file);
        setCheckError(problem);
        if (!problem) send.mutate(file);
      }}
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.forecasts.attachments.fileTitle')}>
          <Input maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field
          label={t('finance.forecasts.attachments.file')}
          hint={
            file ? `${file.name} · ${fmt.bytes(file.size)}` : t('purchasing.attachments.limits')
          }
          required
        >
          <Input
            type="file"
            required
            accept={ATTACHMENT_ACCEPT}
            onChange={(ev) => {
              setFile(ev.target.files?.[0] ?? null);
              setCheckError(null);
            }}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
